/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useConversationStatistics } from '@brands/hooks/useConversationStatistics';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { IConversationStatistics } from '@brands/services/communication/listConversationStatistics';
import { selectAuth } from '@brands/store/selectors/auth';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { GroupSummary } from '../../../../services/patient_plan/types/types';
import care from './Images/care.svg';
import Dashboard from './Images/dashboard.svg';
import insurance from './Images/insurance.svg';
import messages from './Images/messages.svg';
import pharmacy from './Images/pharmacy.svg';
import plan from './Images/plan.svg';
import profile from './Images/profile.svg';
import schedule from './Images/schedule.svg';
import support from './Images/support.svg';
import styles from './styles.module.scss';
import caseIcon from './Images/case.svg';
import userIcon from './Images/user.svg';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';

interface SidebarProps {
  closeMenu: boolean;
  currentPlan: GroupSummary | null;
}

interface MenuItem {
  name: string;
  path: string;
  icon: string;
  external?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ closeMenu, currentPlan }) => {
  const location = useLocation();
  const roleNavigate = useRoleNavigate();
  const { statistics } = useConversationStatistics();

  const hasUnreadMessages = (stats: IConversationStatistics[]): boolean =>
    stats.some((stat) => stat.unread_message_count > 0);

  const unreadCount = useMemo(() => statistics.filter((s) => s.unread_message_count > 0).length, [statistics]);

  const myPlanEnabled = !!currentPlan;

  const { userInfo } = useAppSelector(selectAuth);

  const ZENDESK_HELP_URL = 'https://dentistryone.zendesk.com/hc/en-us';

  const adminLikeMenu: MenuItem[] = [
    { name: 'Cases', path: '/dashboard', icon: caseIcon },
    { name: 'Users', path: '/dashboard#users', icon: userIcon },
    { name: 'Clients', path: '/dashboard#clients', icon: userIcon },
    { name: 'Audit trail', path: '/dashboard#audit-trail', icon: schedule },
    {
      name: 'Support',
      path: ZENDESK_HELP_URL,
      icon: support,
      external: true,
    },
  ];

  const MENU_BY_ROLE: Record<string, MenuItem[]> = {
    [UserRoleName.Patient]: [
      { name: 'Dashboard', path: '/dashboard', icon: Dashboard },
      { name: 'Messages', path: '/messages', icon: messages },
      { name: 'My Care', path: '/my-care', icon: care },
      { name: 'Profile', path: '/my-profile', icon: profile },
      { name: 'My Plan', path: '/my-plan', icon: plan },
      { name: 'Insurance', path: '/insurance', icon: insurance },
      { name: 'Pharmacy', path: '/pharmacy', icon: pharmacy },
      {
        name: 'Support',
        path: ZENDESK_HELP_URL,
        icon: support,
        external: true,
      },
    ],

    [UserRoleName.Provider]: [
      { name: 'Cases', path: '/dashboard', icon: caseIcon },
      { name: 'Messages', path: '/messages', icon: messages },
      { name: 'Profile', path: '/my-profile', icon: profile },
      {
        name: 'Schedule',
        path: './scheduleAvailability',
        icon: schedule,
      },
      {
        name: 'Support',
        path: ZENDESK_HELP_URL,
        icon: support,
        external: true,
      },
    ],

    [UserRoleName.CareAdvisor]: [
      { name: 'Cases', path: '/dashboard', icon: caseIcon },
      { name: 'Users', path: '/users', icon: userIcon },
      { name: 'Messages', path: '/messages', icon: messages },
      { name: 'Schedule', path: '/scheduleAvailability', icon: schedule },
      {
        name: 'Support',
        path: ZENDESK_HELP_URL,
        icon: support,
        external: true,
      },
    ],

    [UserRoleName.Collector]: [
      { name: 'Cases', path: '/dashboard', icon: caseIcon },
      { name: 'Users', path: '/users', icon: userIcon },
      { name: 'Messages', path: '/messages', icon: messages },
      {
        name: 'Support',
        path: ZENDESK_HELP_URL,
        icon: support,
        external: true,
      },
    ],

    [UserRoleName.Admin]: adminLikeMenu,
    [UserRoleName.SuperAdmin]: adminLikeMenu,
    [UserRoleName.OrganizationAdmin]: adminLikeMenu,
  };

  const menuItems = useMemo(() => {
    const role = userInfo?.role?.name as string;
    let items = (MENU_BY_ROLE[role] ?? []).map((item) => ({ ...item }));

    // Example: My Plan depends on currentPlan
    if (!currentPlan) {
      items = items.filter((item) => item.name !== 'My Plan');
    }

    return items;
  }, [userInfo?.role?.name, currentPlan]);

  const handleNavigate = (path: string, disabled?: boolean) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled) return;
    if (`${location.pathname}${location.hash}` !== path) {
      roleNavigate(path);
    }
  };

  if (!myPlanEnabled) {
    const index = menuItems.findIndex((item) => item.name === 'My Plan');
    if (index !== -1) {
      menuItems.splice(index, 1);
    }
  } else {
    const exists = menuItems.some((item) => item.name === 'My Plan');
    if (!exists) {
      menuItems.splice(4, 0, { name: 'My Plan', path: '/my-plan', icon: plan });
    }
  }

  const supportItem = menuItems.find((item) => item.external === true) ?? null;
  const primaryItems = menuItems.filter((item) => item.external !== true);

  return (
    // data-sidebar-rail / data-active below are stable hooks for the design
    // conformance runner. The CSS-module class names are content-hashed, so they
    // change on every build and cannot anchor a spec.
    <div
      className={closeMenu ? `${styles.sidebar} ${styles.active}` : styles.sidebar}
      data-sidebar-rail
    >
      <div className={closeMenu ? `${styles.contentsContainer} ${styles.active}` : styles.contentsContainer}>
        <ul className={styles.menuList}>
          <div className={styles.menuTop}>
            {primaryItems.map((item, index) => {
              const normalizedPath = item.path.replace('./', '/');
              const [pathOnly, hashOnly] = normalizedPath.split('#');
              const isHashRoute = Boolean(hashOnly);
              const isActive = isHashRoute
                ? location.pathname.endsWith(pathOnly) && location.hash === `#${hashOnly}`
                : location.pathname.endsWith(pathOnly) &&
                  (!location.hash || location.hash === '' || location.hash === '#cases');
              const isMyPlan = item.name === 'My Plan';
              const disabled = isMyPlan && !myPlanEnabled;

              return (
                <li
                  key={`${item.name}-${item.path}`}
                  className={`${isActive ? styles.active : ''} ${disabled ? styles.disabledItem : ''} fs-unmask`}
                  data-testid={`sidebar-menu-list-${index}`}
                  data-nav-item={item.name}
                  data-active={isActive}
                  onClick={handleNavigate(item.path, disabled)}
                >
                  {item.name === 'Messages' && hasUnreadMessages(statistics) && (
                    <div className={styles.notificationUnreadCountPatient}>{unreadCount}</div>
                  )}

                  <img
                    src={item.icon}
                    alt={item.name}
                    className={closeMenu ? styles.close : styles.open}
                    style={{
                      marginLeft: item.name === 'Pharmacy' ? '6px' : undefined,
                      marginRight: item.name === 'Insurance' ? '5px' : undefined,
                    }}
                  />

                  <a href={item.path} aria-disabled={disabled || undefined} className={styles.itemLabel}>
                    {item.name}
                  </a>
                </li>
              );
            })}
          </div>
          {supportItem ? (
            <div className={styles.menuBottom}>
              <li key={supportItem.path} className="fs-unmask" onClick={() => window.open(supportItem.path, '_blank')}>
                <img src={supportItem.icon} alt={supportItem.name} className={closeMenu ? styles.close : styles.open} />

                <a
                  href={supportItem.path}
                  className={styles.itemLabel}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.preventDefault()}
                >
                  {supportItem.name}
                </a>
              </li>
            </div>
          ) : null}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
